import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import styled from 'styled-components';
import { ANALYTIC_PANEL_COLORS } from 'utils/constants/analyticPanelColors';
import { convertPrice } from 'utils/convertPrice';
import { stringToNumberOrZero } from 'utils/formatters/stringToNumberOrZero';
import { CostStructureItem, ReportData } from 'utils/types/AnalyticPanelReport';

import { PDF_EXPORT_CLASS } from './pdfExportClass';

const ReportSection = styled.div`
    background: var(--color-white);
    border-radius: var(--box-border-radius);
    padding: 24px;
    margin-bottom: 20px;

    .${PDF_EXPORT_CLASS} & {
        border: 1px solid var(--pdf-card-border);
    }
`;

const SectionTitle = styled.h2`
    font-size: 16px;
    font-weight: 600;
    color: var(--color-primary);
    margin-bottom: 16px;
`;

const TwoColumnGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;

    @media (max-width: 992px) {
        grid-template-columns: 1fr;
    }

    .${PDF_EXPORT_CLASS} & {
        grid-template-columns: 1fr 1fr;
    }
`;

const CostTableContainer = styled.div`
    overflow-x: auto;
`;

const CostTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th, td {
        padding: 12px 16px;
        text-align: left;
        font-size: 13px;
        white-space: nowrap;
    }

    th {
        font-weight: 600;
        color: var(--color-primary);
        border-bottom: 2px solid var(--color-alt-second);
    }

    td {
        border-bottom: 1px solid var(--color-alt-second);
    }

    tr:last-child td {
        border-bottom: none;
        font-weight: 600;
    }

    @media (max-width: 576px) {
        th, td {
            padding: 8px 6px;
            font-size: 11px;
        }
    }
`;

const ColorDot = styled.span<{ $color: string }>`
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    margin-right: 8px;
`;

const PieChartContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 250px;
`;

const TableRow = styled.tr`
    background: transparent;

    &.active {
        background: var(--color-alt-second);
    }
`;

const TdRight = styled.td`
    text-align: right;
`;

interface CostStructureSectionProps {
    data: ReportData;
    isAnimationActive?: boolean;
}

const useResponsivePieSize = () => {
    const [size, setSize] = useState({ radius: 160, height: 380 });

    useEffect(() => {
        const update = () => {
            if (window.innerWidth <= 480) {
                setSize({ radius: 90, height: 220 });
            } else if (window.innerWidth <= 768) {
                setSize({ radius: 120, height: 280 });
            } else {
                setSize({ radius: 160, height: 380 });
            }
        };

        update();
        window.addEventListener('resize', update);

        return () => window.removeEventListener('resize', update);
    }, []);

    return size;
};

export const CostStructureSection: FC<CostStructureSectionProps> = ({ data, isAnimationActive = true }) => {
    const { purchasePrice, costs } = data;
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const { radius: outerRadius, height: chartHeight } = useResponsivePieSize();

    const handleMouseEnter = useCallback((index: number) => setActiveIndex(index), []);
    const handleMouseLeave = useCallback(() => setActiveIndex(null), []);

    const purchasePriceNum = stringToNumberOrZero(purchasePrice);

    const costStructure: CostStructureItem[] = useMemo(() => {
        if (!costs) return [];

        const totalBudget = purchasePriceNum + stringToNumberOrZero(costs.sumOfCosts);

        const purchaseCostsTotal = stringToNumberOrZero(costs.notary) + stringToNumberOrZero(costs.pcc) +
            stringToNumberOrZero(costs.brokerPercent) + stringToNumberOrZero(costs.otherPurchaseCosts);
        const saleCostsTotal = stringToNumberOrZero(costs.photographerAmount) + stringToNumberOrZero(costs.portalFees) +
            stringToNumberOrZero(costs.equipment) + stringToNumberOrZero(costs.homeStaging) +
            stringToNumberOrZero(costs.saleBrokerPercent) + stringToNumberOrZero(costs.otherSaleCosts);
        const operatingCostsTotal = stringToNumberOrZero(costs.maintenanceFees) + stringToNumberOrZero(costs.rentFees);

        const structure: CostStructureItem[] = [
            {
                id: 'purchasePrice',
                label: 'Cena mieszkania',
                value: purchasePriceNum,
                percentage: totalBudget > 0 ? purchasePriceNum / totalBudget * 100 : 0,
                color: ANALYTIC_PANEL_COLORS.purchasePrice
            },
            {
                id: 'renovation',
                label: 'Remont z materiałami',
                value: stringToNumberOrZero(costs.renovation),
                percentage: totalBudget > 0 ? stringToNumberOrZero(costs.renovation) / totalBudget * 100 : 0,
                color: ANALYTIC_PANEL_COLORS.renovation
            },
            {
                id: 'purchaseCosts',
                label: 'Koszty okołozakupowe',
                value: purchaseCostsTotal,
                percentage: totalBudget > 0 ? purchaseCostsTotal / totalBudget * 100 : 0,
                color: ANALYTIC_PANEL_COLORS.purchaseCosts
            },
            {
                id: 'saleCosts',
                label: 'Koszty sprzedaży',
                value: saleCostsTotal,
                percentage: totalBudget > 0 ? saleCostsTotal / totalBudget * 100 : 0,
                color: ANALYTIC_PANEL_COLORS.saleCosts
            },
            {
                id: 'operatingCosts',
                label: 'Koszty eksploatacyjne',
                value: operatingCostsTotal,
                percentage: totalBudget > 0 ? operatingCostsTotal / totalBudget * 100 : 0,
                color: ANALYTIC_PANEL_COLORS.operatingCosts
            },
            {
                id: 'otherCosts',
                label: 'Pozostałe koszty',
                value: stringToNumberOrZero(costs.otherCosts),
                percentage: totalBudget > 0 ? stringToNumberOrZero(costs.otherCosts) / totalBudget * 100 : 0,
                color: ANALYTIC_PANEL_COLORS.otherCosts
            },
            {
                id: 'financingCosts',
                label: 'Koszty finansowania',
                value: stringToNumberOrZero(costs.otherFees),
                percentage: totalBudget > 0 ? stringToNumberOrZero(costs.otherFees) / totalBudget * 100 : 0,
                color: ANALYTIC_PANEL_COLORS.financingCosts
            }
        ];

        return structure;
    }, [purchasePriceNum, costs]);

    const pieData = costStructure
        .filter((item) => item.value > 0)
        .map((item) => ({
            name: item.label,
            value: item.value,
            percentage: `${item.percentage.toFixed(2)}%`,
            color: item.color
        }));

    const handlePieMouseEnter = useCallback((_: unknown, index: number) => {
        const originalIndex = costStructure.findIndex((item) => item.label === pieData[index].name);
        setActiveIndex(originalIndex);
    }, [pieData, costStructure]);

    const totalBudget = purchasePriceNum + stringToNumberOrZero(costs?.sumOfCosts);

    if (!costs) return null;

    return (
        <ReportSection>
            <SectionTitle>Struktura kosztów flipa</SectionTitle>

            <TwoColumnGrid>
                <CostTableContainer>
                    <CostTable>
                        <thead>
                            <tr>
                                <th>Kategoria</th>
                                <th style={{ textAlign: 'right' }}>Wartość</th>
                                <th style={{ textAlign: 'right' }}>Udział</th>
                            </tr>
                        </thead>
                        <tbody>
                            {costStructure.map((item, index) => <TableRow
                                key={item.id}
                                className={clsx({ active: activeIndex === index })}
                                onMouseEnter={() => handleMouseEnter(index)}
                                onMouseLeave={handleMouseLeave}
                            >
                                <td>
                                    <ColorDot $color={item.color} />
                                    {item.label}
                                </td>
                                <TdRight>{convertPrice(item.value)} zł</TdRight>
                                <TdRight>{item.percentage.toFixed(2)}%</TdRight>
                            </TableRow>)}
                            <tr style={{ background: 'var(--color-alt-second)' }}>
                                <td>Całkowity budżet projektu</td>
                                <TdRight>{convertPrice(totalBudget)} zł</TdRight>
                                <TdRight>100%</TdRight>
                            </tr>
                        </tbody>
                    </CostTable>
                </CostTableContainer>

                <PieChartContainer>
                    <ResponsiveContainer width="100%" height={chartHeight}>
                        <PieChart>
                            <Pie
                                isAnimationActive={isAnimationActive}
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={outerRadius}
                                stroke="none"
                                onMouseEnter={handlePieMouseEnter}
                                onMouseLeave={handleMouseLeave}
                            >
                                {pieData.map((entry, index) => <Cell
                                    key={`cell-${index}`}
                                    fill={entry.color}
                                    strokeWidth={
                                        costStructure.findIndex((item) => item.label === entry.name) === activeIndex
                                            ? 3
                                            : 0
                                    }
                                    stroke="white"
                                />)}
                            </Pie>
                            <Tooltip
                                formatter={(value: number, name: string) => [
                                    `${convertPrice(value)} zł`,
                                    name
                                ]}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </PieChartContainer>
            </TwoColumnGrid>
        </ReportSection>
    );
};
